import { Link } from 'react-router-dom';
import Breadcrumbs from '../components/shared/Breadcrumbs';

const AffiliateOffenlegungPage = () => {
  return (
    <div className="py-12" data-testid="affiliate-offenlegung-page">
      <div className="container-wide">
        <Breadcrumbs items={[{ label: 'Affiliate-Offenlegung', href: '/affiliate-offenlegung' }]} />

        <div className="max-w-3xl">
          <h1 className="mb-8">Affiliate-Offenlegung — Transparenz bei ReviewLex.de</h1>

          <div className="prose-reviewlex">
            <div className="card-highlight p-6 mb-8">
              <p className="mb-0 text-slate-700">
                <strong>Zusammenfassung:</strong> Wir verdienen Geld über Affiliate-Links, wenn Sie 
                Services über unsere Links kaufen. Das kostet Sie nichts extra und beeinflusst nicht 
                unsere ehrlichen Bewertungen.
              </p>
            </div>

            <section className="mb-8">
              <h2>Warum diese Offenlegung?</h2>
              <p>
                Transparenz ist uns wichtig. Auf dieser Seite erklären wir, wie ReviewLex.de 
                finanziert wird und wie wir mit Affiliate-Partnerschaften umgehen. Wir möchten, 
                dass Sie genau wissen, wie wir Geld verdienen und wie dies (nicht) unsere 
                Empfehlungen beeinflusst.
              </p>
            </section>

            <section className="mb-8">
              <h2>Wie wir Geld verdienen</h2>
              <p>
                Einige Links auf ReviewLex.de sind sogenannte <strong>Affiliate-Links</strong>. 
                Wenn Sie über einen solchen Link ein Produkt oder eine Dienstleistung kaufen, 
                erhalten wir möglicherweise eine Provision vom Anbieter — <strong>ohne zusätzliche 
                Kosten für Sie</strong>.
              </p>
              <p>
                Diese Provisionen ermöglichen es uns:
              </p>
              <ul>
                <li>Die Website zu betreiben und technisch auf dem neuesten Stand zu halten</li>
                <li>Neue Anbieter zu testen und Inhalte regelmäßig zu aktualisieren</li>
                <li>Hochwertige, unabhängige Ratgeber-Inhalte zu erstellen</li>
                <li>Keine Bezahlschranken oder Werbebanner zu verwenden</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2>Mit welchen Unternehmen arbeiten wir zusammen?</h2>
              <p>
                Wir unterhalten Affiliate-Partnerschaften mit verschiedenen Firmengründungs- und 
                Registered-Agent-Diensten, darunter:
              </p>
              <ul>
                <li><strong>Northwest Registered Agent</strong> — unser Testsieger und primärer Partner</li>
                <li>Weitere LLC-Gründungsdienste, die wir in unseren Vergleichen erwähnen</li>
              </ul>
              <p>
                Nicht jeder von uns erwähnte Dienst ist ein Affiliate-Partner. Wir erwähnen auch 
                Anbieter, mit denen wir keine kommerzielle Beziehung haben, wenn sie für unsere 
                Leser relevant sind.
              </p>
            </section>

            <section className="mb-8">
              <h2>Beeinflussen Affiliate-Partnerschaften unsere Bewertungen?</h2>
              <p>
                <strong>Nein.</strong> Unsere Affiliate-Partnerschaften haben keinen Einfluss auf 
                unsere Bewertungen, Rankings oder Empfehlungen. Alle Bewertungen basieren auf 
                unseren eigenen Tests und Recherchen nach einem standardisierten Bewertungssystem.
              </p>
              <p>
                Konkret bedeutet das:
              </p>
              <ul>
                <li>
                  <strong>Wir bewerten jeden Anbieter nach denselben Kriterien:</strong> Preis-Leistung, 
                  Benutzerfreundlichkeit, Datenschutz, Kundenservice und Funktionsumfang.
                </li>
                <li>
                  <strong>Ein Anbieter kann keine bessere Bewertung kaufen.</strong> Wenn ein 
                  Affiliate-Partner schlechter abschneidet als ein Nicht-Partner, sagen wir das.
                </li>
                <li>
                  <strong>Wir empfehlen nur Services, die wir selbst getestet haben</strong> und 
                  von deren Qualität wir überzeugt sind.
                </li>
                <li>
                  <strong>Wir nennen auch Nachteile.</strong> Jede Bewertung enthält sowohl 
                  Vorteile als auch Nachteile.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2>Wie erkennen Sie Affiliate-Links?</h2>
              <p>
                Auf Seiten mit Affiliate-Links finden Sie einen Hinweis am Anfang des Artikels:
              </p>
              <div className="affiliate-banner my-4">
                <strong>Hinweis:</strong> Dieser Artikel enthält Affiliate-Links. Bei einem Kauf 
                über diese Links erhalten wir eine Provision. <a href="#" className="text-teal-600">Mehr erfahren</a>
              </div>
              <p>
                Affiliate-Links zu Gründungsdiensten enthalten in der Regel:
              </p>
              <ul>
                <li>Call-to-Action-Buttons wie "Jetzt starten" oder "Zum Anbieter"</li>
                <li>Links in Vergleichstabellen zur Anbieter-Website</li>
                <li>Das Attribut <code>rel="sponsored"</code> im HTML-Code</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2>Rechtliche Grundlage</h2>
              <p>
                Diese Offenlegung erfolgt in Übereinstimmung mit:
              </p>
              <ul>
                <li>
                  Den Richtlinien der <strong>US Federal Trade Commission (FTC)</strong> für 
                  Endorsements und Testimonials
                </li>
                <li>
                  Der deutschen <strong>Kennzeichnungspflicht für Werbung</strong> gemäß dem 
                  Telemediengesetz (TMG) und dem Gesetz gegen den unlauteren Wettbewerb (UWG)
                </li>
                <li>
                  Den <strong>Affiliate-Programm-Richtlinien</strong> unserer Partner
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2>Unser Versprechen an Sie</h2>
              <p>
                Wir versprechen Ihnen:
              </p>
              <ul>
                <li>
                  <strong>Ehrlichkeit:</strong> Unsere Bewertungen spiegeln unsere tatsächlichen 
                  Erfahrungen wider.
                </li>
                <li>
                  <strong>Transparenz:</strong> Wir offenbaren unsere kommerziellen Beziehungen.
                </li>
                <li>
                  <strong>Qualität vor Provision:</strong> Wir empfehlen nur Services, die wir 
                  auch unseren Freunden empfehlen würden.
                </li>
                <li>
                  <strong>Keine versteckten Kosten:</strong> Affiliate-Links kosten Sie nie 
                  mehr als der direkte Kauf beim Anbieter.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2>Fragen?</h2>
              <p>
                Wenn Sie Fragen zu unseren Affiliate-Partnerschaften haben, kontaktieren Sie uns 
                gerne unter{' '}
                <a href="mailto:kontakt@reviewlex.de" className="text-teal-600 hover:text-teal-700">
                  kontakt@reviewlex.de
                </a>{' '}
                oder über unser{' '}
                <Link to="/kontakt" className="text-teal-600 hover:text-teal-700">
                  Kontaktformular
                </Link>.
              </p>
            </section>

            <p className="text-sm text-slate-500 mt-12">
              Stand: April 2026
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateOffenlegungPage;
